import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withFirebase } from '../../firebase';
import { CChartBar } from '@coreui/react-chartjs';
import { Chart, registerables } from 'chart.js';
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CFormSelect,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTabContent,
  CTabPane,
} from '@coreui/react';
import axios from 'axios';
import CIcon from '@coreui/icons-react';
import { cibBitcoin } from '@coreui/icons';
import PortfolioTracker from './PortfolioTracker';
import StocksPanel from './StocksPanel';

Chart.register(...registerables);

const API_URL = 'https://api.exchange.coinbase.com';
const DEFAULT_PAGE_SIZE = 10;
const TABLE_SORT_OPTIONS = [
  { value: 'id', label: 'Symbol' },
  { value: 'name', label: 'Name' },
  { value: 'min_size', label: 'Min Size' },
  { value: 'max_precision', label: 'Precision' },
];

const parseFloatSafe = (value, fallback = 0) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const sortRows = (rows, sortKey, direction) => {
  const factor = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const aVal = a?.[sortKey];
    const bVal = b?.[sortKey];
    const isNumeric = sortKey === 'min_size' || sortKey === 'max_precision';

    if (isNumeric) {
      return (parseFloatSafe(aVal) - parseFloatSafe(bVal)) * factor;
    }
    return String(aVal || '').localeCompare(String(bVal || '')) * factor;
  });
};

const CryptoDashboard = () => {
  const [activeTab, setActiveTab] = useState('crypto');

  const [cryptoData, setCryptoData] = useState([]);
  const [bitcoinData, setBitcoinData] = useState(null);
  const [bitcoinPriceData, setBitcoinPriceData] = useState(null);
  const [topMovers, setTopMovers] = useState([]);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const fetchDashboardData = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    else setInitialLoading(true);

    setError('');
    try {
      const [currenciesRes, bitcoinRes, tickerRes, productsRes] = await Promise.allSettled([
        axios.get(`${API_URL}/currencies`),
        axios.get(`${API_URL}/currencies/BTC`),
        axios.get(`${API_URL}/products/BTC-USD/ticker`),
        axios.get(`${API_URL}/products`),
      ]);

      const failures = [];

      if (currenciesRes.status === 'fulfilled') {
        const allCurrencies = Array.isArray(currenciesRes.value.data) ? currenciesRes.value.data : [];
        const activeCryptos = allCurrencies.filter(
          (currency) => currency?.status === 'online' && currency?.details?.type !== 'fiat'
        );
        setCryptoData(activeCryptos);
      } else {
        failures.push('cryptocurrency list');
      }

      if (bitcoinRes.status === 'fulfilled') {
        setBitcoinData(bitcoinRes.value.data || null);
      } else {
        failures.push('bitcoin metadata');
      }

      if (tickerRes.status === 'fulfilled') {
        setBitcoinPriceData(tickerRes.value.data || null);
      } else {
        failures.push('bitcoin ticker');
      }

      if (productsRes.status === 'fulfilled') {
        const allProducts = Array.isArray(productsRes.value.data) ? productsRes.value.data : [];
        const usdProducts = allProducts
          .filter((p) => p?.quote_currency === 'USD' && p?.status === 'online' && !p?.trading_disabled)
          .slice(0, 10)
          .map((p) => p.id);

        const tickerJobs = await Promise.allSettled(
          usdProducts.map((productId) => axios.get(`${API_URL}/products/${productId}/ticker`))
        );

        const movers = tickerJobs
          .filter((job) => job.status === 'fulfilled')
          .map((job) => job.value?.data)
          .filter((ticker) => ticker?.product_id)
          .map((ticker) => {
            const price = parseFloatSafe(ticker.price);
            const open = parseFloatSafe(ticker.open);
            const changePct = open > 0 ? ((price - open) / open) * 100 : 0;
            return { productId: ticker.product_id, price, changePct };
          })
          .sort((a, b) => Math.abs(b.changePct) - Math.abs(a.changePct))
          .slice(0, 5);

        setTopMovers(movers);
      } else {
        failures.push('top movers');
      }

      if (failures.length) {
        setError(`Some data failed to load: ${failures.join(', ')}.`);
      }
    } catch (err) {
      setError('Failed to load crypto dashboard data.');
      console.error('CryptoDashboard fetch error:', err);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // Load crypto when that tab is active (or once on first visit)
    if (activeTab === 'crypto') {
      fetchDashboardData();
    }
  }, [activeTab, fetchDashboardData]);

  const filteredData = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return cryptoData;
    return cryptoData.filter(
      (row) =>
        String(row?.id || '').toLowerCase().includes(term) ||
        String(row?.name || '').toLowerCase().includes(term)
    );
  }, [cryptoData, query]);

  const sortedData = useMemo(
    () => sortRows(filteredData, sortKey, sortDirection),
    [filteredData, sortKey, sortDirection]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedData.length / pageSize)),
    [sortedData.length, pageSize]
  );

  const paginatedData = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [query, sortKey, sortDirection, pageSize]);

  const renderBitcoinCard = () => {
    if (!bitcoinData || !bitcoinPriceData) return null;

    const bid = parseFloatSafe(bitcoinPriceData.bid);
    const ask = parseFloatSafe(bitcoinPriceData.ask);
    const last = parseFloatSafe(bitcoinPriceData.price);
    const spreadPct = ask > 0 ? ((ask - bid) / ask) * 100 : 0;
    const dayOpen = parseFloatSafe(bitcoinPriceData.open);
    const dayChangePct = dayOpen > 0 ? ((last - dayOpen) / dayOpen) * 100 : 0;

    return (
      <CCard className="mb-4 crypto-card">
        <CCardHeader className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <CIcon icon={cibBitcoin} size="xl" className="me-3" />
            <h3 className="mb-0">
              {bitcoinData.name} ({bitcoinData.id})
            </h3>
          </div>
          <CBadge color={dayChangePct >= 0 ? 'success' : 'danger'}>
            {dayChangePct >= 0 ? '+' : ''}
            {dayChangePct.toFixed(2)}%
          </CBadge>
        </CCardHeader>
        <CCardBody>
          <div className="d-flex justify-content-between mb-4 gap-3 flex-wrap">
            <div className="crypto-price-box flex-grow-1">
              <div className="label">Current Price</div>
              <div className="value">${last.toFixed(2)}</div>
            </div>
            <div className="crypto-price-box flex-grow-1">
              <div className="label">Spread</div>
              <div className="value">{spreadPct.toFixed(3)}%</div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-6">
              <div className="crypto-stat-box">
                <div className="label">Bid</div>
                <div className="value">${bid.toFixed(2)}</div>
              </div>
            </div>
            <div className="col-6">
              <div className="crypto-stat-box">
                <div className="label">Ask</div>
                <div className="value">${ask.toFixed(2)}</div>
              </div>
            </div>
            <div className="col-12">
              <div className="crypto-stat-box">
                <div className="label">Last Trade Size</div>
                <div className="value">{parseFloatSafe(bitcoinPriceData.last_size).toFixed(6)} BTC</div>
              </div>
            </div>
          </div>
        </CCardBody>
      </CCard>
    );
  };

  const renderTopMovers = () => {
    if (!topMovers.length) return null;
    return (
      <CCard className="mb-4 crypto-card">
        <CCardHeader>
          <h3 className="mb-0">Top Movers (USD Pairs)</h3>
        </CCardHeader>
        <CCardBody>
          <CChartBar
            data={{
              labels: topMovers.map((m) => m.productId),
              datasets: [
                {
                  label: '24h % Change',
                  data: topMovers.map((m) => Number(m.changePct.toFixed(2))),
                  backgroundColor: topMovers.map((m) =>
                    m.changePct >= 0 ? 'rgba(16, 185, 129, 0.75)' : 'rgba(239, 68, 68, 0.75)'
                  ),
                  borderColor: topMovers.map((m) =>
                    m.changePct >= 0 ? 'rgba(16, 185, 129, 1)' : 'rgba(239, 68, 68, 1)'
                  ),
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { ticks: { color: '#94A3B8' }, grid: { color: 'rgba(255,255,255,0.06)' } },
                y: { ticks: { color: '#94A3B8' }, grid: { color: 'rgba(255,255,255,0.06)' } },
              },
            }}
            style={{ height: 260 }}
          />
        </CCardBody>
      </CCard>
    );
  };

  const renderCryptoTable = () => (
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <h3 className="mb-0">Available Cryptocurrencies</h3>
        <div className="d-flex gap-2 flex-wrap">
          <CFormInput
            size="sm"
            placeholder="Search symbol or name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <CFormSelect size="sm" value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={{ minWidth: 140 }}>
            {TABLE_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                Sort: {opt.label}
              </option>
            ))}
          </CFormSelect>
          <CFormSelect
            size="sm"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
            style={{ minWidth: 120 }}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </CFormSelect>
          <CFormSelect size="sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ minWidth: 100 }}>
            <option value={10}>10/page</option>
            <option value={25}>25/page</option>
            <option value={50}>50/page</option>
          </CFormSelect>
        </div>
      </CCardHeader>
      <CCardBody>
        <CTable hover responsive className="crypto-table">
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell>Currency</CTableHeaderCell>
              <CTableHeaderCell>Name</CTableHeaderCell>
              <CTableHeaderCell>Min Size</CTableHeaderCell>
              <CTableHeaderCell>Precision</CTableHeaderCell>
              <CTableHeaderCell>Status</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {paginatedData.map((crypto) => (
              <CTableRow key={crypto.id}>
                <CTableDataCell>
                  <strong>{crypto.id}</strong>
                </CTableDataCell>
                <CTableDataCell>{crypto.name}</CTableDataCell>
                <CTableDataCell>{crypto.min_size || '-'}</CTableDataCell>
                <CTableDataCell>{crypto.max_precision || '-'}</CTableDataCell>
                <CTableDataCell>
                  <CBadge color={crypto.status === 'online' ? 'success' : 'danger'}>{crypto.status}</CBadge>
                </CTableDataCell>
              </CTableRow>
            ))}
            {!paginatedData.length && (
              <CTableRow>
                <CTableDataCell colSpan={5} className="text-center mb-2">
                  No currencies match your filters.
                </CTableDataCell>
              </CTableRow>
            )}
          </CTableBody>
        </CTable>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small className="mb-2">
            Showing {paginatedData.length} of {sortedData.length} results
          </small>
          <div className="d-flex gap-2 align-items-center">
            <CButton size="sm" color="secondary" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Prev
            </CButton>
            <small className="mb-2">
              Page {Math.min(page, totalPages)} / {totalPages}
            </small>
            <CButton
              size="sm"
              color="secondary"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>
  );

  const renderCryptoTab = () => {
    if (initialLoading) {
      return (
        <CCard className="text-center p-4">
          <div className="d-flex justify-content-center align-items-center gap-2">
            <CSpinner color="primary" />
            <span>Loading cryptocurrency data...</span>
          </div>
        </CCard>
      );
    }

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h3 className="mb-0">Cryptocurrency Dashboard</h3>
          <CButton color="info" variant="outline" onClick={() => fetchDashboardData({ isRefresh: true })} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </CButton>
        </div>

        {error && (
          <CAlert color="warning" className="mb-4">
            {error}
          </CAlert>
        )}

        <CRow className="mb-4">
          <CCol sm={6} lg={3}>
            <CCard className="crypto-card h-100">
              <CCardBody>
                <div className="crypto-stat-label">Active Crypto Currencies</div>
                <div className="h4 mb-0">{cryptoData.length}</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol sm={6} lg={3}>
            <CCard className="crypto-card h-100">
              <CCardBody>
                <div className="crypto-stat-label">Top Movers Tracked</div>
                <div className="h4 mb-0">{topMovers.length}</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol sm={6} lg={3}>
            <CCard className="crypto-card h-100">
              <CCardBody>
                <div className="crypto-stat-label">BTC Last Price</div>
                <div className="h4 mb-0">
                  {bitcoinPriceData?.price ? `$${parseFloatSafe(bitcoinPriceData.price).toFixed(2)}` : 'N/A'}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol sm={6} lg={3}>
            <CCard className="crypto-card h-100">
              <CCardBody>
                <div className="crypto-stat-label">BTC Pair</div>
                <div className="h4 mb-0">{bitcoinPriceData?.product_id || 'BTC-USD'}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <CRow>
          <CCol lg={6} className="mb-4">
            {renderBitcoinCard()}
          </CCol>
          <CCol lg={6} className="mb-4">
            <PortfolioTracker />
          </CCol>
          <CCol xs={12}>{renderTopMovers()}</CCol>
          <CCol xs={12}>{renderCryptoTable()}</CCol>
        </CRow>
      </>
    );
  };

  return (
    <div className="crypto-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="mb-0">Market</h2>
      </div>

      <CNav variant="tabs" role="tablist" className="mb-4">
        <CNavItem>
          <CNavLink
            active={activeTab === 'crypto'}
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('crypto')}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTab('crypto')}
            style={{ cursor: 'pointer' }}
          >
            Cryptocurrency
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            active={activeTab === 'stocks'}
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('stocks')}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTab('stocks')}
            style={{ cursor: 'pointer' }}
          >
            Stocks
          </CNavLink>
        </CNavItem>
      </CNav>

      <CTabContent>
        <CTabPane role="tabpanel" visible={activeTab === 'crypto'}>
          {activeTab === 'crypto' && renderCryptoTab()}
        </CTabPane>
        <CTabPane role="tabpanel" visible={activeTab === 'stocks'}>
          {activeTab === 'stocks' && <StocksPanel />}
        </CTabPane>
      </CTabContent>
    </div>
  );
};

export default withFirebase(CryptoDashboard);
